import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import { formatQuantity, type OrderWithItems } from '@tailoring-crm/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@tailoring-crm/ui';
import { useProducts } from '../../lib/products/use-products.js';
import { useUpdateOrder } from '../../lib/orders/use-orders.js';
import { formatCurrency } from '../../lib/format.js';
import {
  ProductLinesEditor,
  draftProductLinesPaise,
  fromProductLines,
  toProductLineInputs,
  type DraftProductLine,
} from './product-lines-editor.js';

/**
 * What an order sold over the counter, and the one place to change it after booking.
 *
 * The edit REPLACES the whole list, the way the API takes it: the server nets the old lines
 * against the new, so re-saving an unchanged basket moves no stock and removing a line puts its
 * fabric back on the shelf. A cancelled order cannot be edited — its lines are the record of what
 * was sold before it was called off.
 */
export function ProductLinesCard({
  order,
  canEdit,
}: {
  order: OrderWithItems;
  canEdit: boolean;
}): ReactElement | null {
  const { t } = useTranslation();
  const { toast } = useToast();
  const update = useUpdateOrder(order.id);
  const [editing, setEditing] = useState(false);
  const [lines, setLines] = useState<DraftProductLine[]>([]);
  const products = useProducts({}, { enabled: editing });

  const editable = canEdit && !order.cancelledAt;
  if (order.productLines.length === 0 && !editable) return null;

  const open = (): void => {
    setLines(fromProductLines(order.productLines));
    setEditing(true);
  };

  const save = async (): Promise<void> => {
    try {
      const updated = await update.mutateAsync({ productLines: toProductLineInputs(lines) });
      setEditing(false);
      toast({ title: t('orders.productLines.saved'), variant: 'success' });
      for (const warning of updated.stockWarnings ?? []) {
        toast({
          title: t('orders.productLines.stockShort', { name: warning.name }),
          variant: 'warning',
        });
      }
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : t('error.unexpected'),
        variant: 'destructive',
      });
    }
  };

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-muted-foreground">
          {t('orders.productLines.heading', { count: order.productLines.length })}
        </h3>
        {editable ? (
          <Button size="sm" variant="outline" onClick={open}>
            <Pencil aria-hidden="true" />
            {order.productLines.length === 0
              ? t('orders.productLines.add')
              : t('orders.productLines.edit')}
          </Button>
        ) : null}
      </div>

      {order.productLines.length > 0 ? (
        <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
          {order.productLines.map((line) => (
            <li key={line.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
              <div>
                <p className="font-medium">{line.name}</p>
                <p className="text-sm text-muted-foreground">
                  {t('orders.productLines.quantityAtPrice', {
                    quantity: formatQuantity(line.quantity, 'metre'),
                    unit: t(`sourcing.unit.${line.unit}`),
                    price: formatCurrency(line.unitPricePaise),
                  })}
                </p>
              </div>
              <p className="font-semibold tabular-nums">{formatCurrency(line.linePaise)}</p>
            </li>
          ))}
        </ul>
      ) : null}

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {t('orders.productLines.editTitle', { number: order.orderNumber })}
            </DialogTitle>
            <DialogDescription>{t('orders.productLines.editHint')}</DialogDescription>
          </DialogHeader>
          <ProductLinesEditor
            lines={lines}
            onChange={setLines}
            products={products.data?.items ?? []}
            idPrefix="edit-product-line"
          />
          <p className="text-right text-sm font-semibold tabular-nums">
            {formatCurrency(draftProductLinesPaise(lines))}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(false)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={update.isPending} onClick={() => void save()}>
              {t('orders.productLines.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
